/**
 * VenueCoverageMap — visual mapping from a safe venue's category to the
 * pre-meet compliance prompts it inherently helps satisfy.
 *
 * Two render modes:
 *   - compact (default): a 4-pill summary (one per prompt) with status
 *     dots. Used inside the venue card in StepWhere.
 *   - detailed: a list of prompt → coverage explanations. Used inside
 *     StepCompliance to show "this venue covers X for you, you still
 *     need to acknowledge Y yourself".
 *
 * Presentation-only — actual acknowledgement still lives on the
 * member via StepCompliance checkboxes.
 */
import { CheckCircle2, CircleDashed, Circle } from "lucide-react";
import {
  getVenueCoverage,
  coverageScore,
  PROMPT_LABELS,
  type CompliancePromptKey,
} from "@/lib/safety/venueCoverage";

interface Props {
  category: string | null | undefined;
  variant?: "compact" | "detailed";
  /** Highlight which prompts the user still has not acknowledged.
   *  Detailed view only. */
  unacknowledged?: CompliancePromptKey[];
}

const LEVEL_ICON = {
  covered: CheckCircle2,
  partial: CircleDashed,
  self: Circle,
} as const;

const LEVEL_TONE = {
  covered: "text-success",
  partial: "text-amber-600 dark:text-amber-400",
  self: "text-muted-foreground",
} as const;

const LEVEL_LABEL = {
  covered: "Venue covers",
  partial: "Partial",
  self: "On you",
} as const;

const VenueCoverageMap = ({
  category,
  variant = "compact",
  unacknowledged = [],
}: Props) => {
  const items = getVenueCoverage(category);

  if (variant === "compact") {
    const score = coverageScore(category);
    return (
      <div className="mt-2 space-y-1">
        <div className="flex items-center justify-between text-[10px] uppercase tracking-wider text-muted-foreground">
          <span>Compliance coverage</span>
          <span className="tabular-nums">
            {score.covered}/{score.total} covered
            {score.partial > 0 && ` · ${score.partial} partial`}
          </span>
        </div>
        <ul className="flex flex-wrap gap-1">
          {items.map((item) => {
            const Icon = LEVEL_ICON[item.level];
            return (
              <li
                key={item.key}
                title={item.note}
                className={`inline-flex items-center gap-1 rounded-full border border-border bg-background/60 px-1.5 py-0.5 text-[10px] ${LEVEL_TONE[item.level]}`}
              >
                <Icon className="h-2.5 w-2.5" />
                <span>{PROMPT_LABELS[item.key]}</span>
              </li>
            );
          })}
        </ul>
      </div>
    );
  }

  // Detailed
  return (
    <div className="rounded-xl border border-border bg-secondary/30 p-3 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Venue coverage map
        </p>
        <span className="text-[10px] text-muted-foreground">
          How this place helps each prompt below
        </span>
      </div>
      <ul className="space-y-1.5">
        {items.map((item) => {
          const Icon = LEVEL_ICON[item.level];
          const isOpen = unacknowledged.includes(item.key);
          return (
            <li
              key={item.key}
              className={`flex items-start gap-2 rounded-lg border px-2.5 py-2 ${
                isOpen
                  ? "border-amber-500/30 bg-amber-500/5"
                  : "border-border bg-card"
              }`}
            >
              <Icon className={`h-4 w-4 mt-0.5 shrink-0 ${LEVEL_TONE[item.level]}`} />
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-medium">
                    {PROMPT_LABELS[item.key]}
                  </p>
                  <span
                    className={`text-[10px] uppercase tracking-wider ${LEVEL_TONE[item.level]}`}
                  >
                    {LEVEL_LABEL[item.level]}
                  </span>
                </div>
                <p className="mt-0.5 text-[11px] text-muted-foreground">
                  {item.note}
                </p>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default VenueCoverageMap;
